// ─────────────────────────────────────────────────────────────
// KeywordGod — Frontend API client
//   - fetch 래퍼 (credentials include, JSON 자동)
//   - 응답은 ApiResponse<T> = { success: true, data } | { success: false, error }
// ─────────────────────────────────────────────────────────────

import type {
  Announcement,
  AnnouncementLevel,
  ApiResponse,
  BulkResultItem,
  ChannelDistribution,
  FavoriteItem,
  GoldenTodayResponse,
  KeywordAnalysisResponse,
  MemberSummary,
  RankChannel,
  RankCheckResult,
  RankHistoryResponse,
  RankTargetItem,
  SearchHistoryItem,
  SeasonEvent,
  SerpItem,
  UsageStatsRow,
  User,
  UserRole,
  UserStatus,
} from '@keywordgod/shared';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!json || !('success' in json)) {
    throw new Error(`서버 응답이 비정상입니다 (${res.status})`);
  }
  if (!json.success) {
    const err = new Error(json.error.message);
    (err as Error & { code?: string }).code = json.error.code;
    throw err;
  }
  return json.data;
}

export const api = {
  health: () => http<{ env: string; ts: string }>('/api/health'),

  stats: {
    keywords: () =>
      http<{ total: number; max: number; progress: number; paused: boolean }>(
        '/api/stats/keywords',
      ),
  },

  season: {
    now: (days = 21) => http<SeasonEvent[]>(`/api/season/now?days=${days}`),
    upcoming: (days = 60) => http<SeasonEvent[]>(`/api/season/upcoming?days=${days}`),
    all: (category?: string) =>
      http<SeasonEvent[]>(
        `/api/season/all${category ? `?category=${encodeURIComponent(category)}` : ''}`,
      ),
  },

  /** Phase 39: 1page 점유 분석 + 빈자리 진단 (Gemini 사용자 키) */
  serpAnalysis: {
    analyze: (params: { keyword: string; geminiApiKey: string; model?: string }) =>
      http<{
        keyword: string;
        meta: {
          godScore: number | null;
          grade: string | null;
          totalSearch: number | null;
          saturation: number | null;
          compIdx: string | null;
          blogTotal: number | null;
          inOurDb: boolean;
        };
        serp: {
          blog: SerpItem[];
          cafe: SerpItem[];
          news: SerpItem[];
        };
        analysis: {
          commonTopics: string[];
          missingAngles: string[];
          topRecommendedTitles: string[];
          rivalKeywords: Array<{ keyword: string; reason: string }>;
          diagnosis: string;
        };
      }>('/api/serp-analysis/analyze', {
        method: 'POST',
        body: JSON.stringify(params),
      }),
  },

  /** Phase 38: 블로그 코치 — 본인 블로그 + 본인 Gemini API 키 기반 실시간 추천 30개 */
  blogCoach: {
    analyze: (params: { blogUrl: string; geminiApiKey: string; model?: string }) =>
      http<{
        blog: {
          userId: string;
          blogName: string | null;
          postCount: number;
          recentPosts: Array<{
            title: string;
            category: string | null;
            pubDate: string;
            link: string;
          }>;
        };
        profile: {
          summary: string;
          topic: string;
          audience: string;
          style: string;
          categories: string[];
        };
        recommendations: Array<{
          keyword: string;
          reason: string;
          godScore: number | null;
          grade: string | null;
          totalSearch: number | null;
          saturation: number | null;
          inOurDb: boolean;
        }>;
        enrichmentCount: number;
        analysis: {
          recentPostCount: number;
          olderPostCount: number;
          recentCutoff: string;
          upcomingSeasons: Array<{
            name: string;
            emoji: string | null;
            state: 'active' | 'upcoming' | 'past';
            daysUntilStart: number;
            startsMd: string;
          }>;
        };
      }>('/api/blog-coach/analyze', {
        method: 'POST',
        body: JSON.stringify(params),
      }),

    /** Phase 41: 경쟁 블로그 격차 분석 */
    compare: (params: {
      myBlogUrl: string;
      rivalBlogUrls: string[];
      geminiApiKey: string;
      model?: string;
    }) =>
      http<{
        me: { userId: string; blogName: string | null; postCount: number };
        rivals: Array<{ userId: string; blogName: string | null; postCount: number }>;
        rivalErrors: string[];
        analysis: {
          myProfile: {
            summary: string;
            strengths: string[];
            weaknesses: string[];
          };
          rivalProfiles: Array<{
            blogId: string;
            summary: string;
            strengths: string[];
          }>;
          gapKeywords: Array<{
            keyword: string;
            reason: string;
            rivalSource: string;
          }>;
          actionPlan: string[];
        };
      }>('/api/blog-coach/compare', {
        method: 'POST',
        body: JSON.stringify(params),
      }),
  },

  /** Phase 28: 카테고리 기반 키워드 브라우징 (Phase 29: 미분석 포함 + 카운트 분리) */
  categories: {
    list: () =>
      http<{
        categories: Array<{
          id: string;
          label: string;
          emoji: string;
          description: string;
          seedCount: number;
          analyzedCount: number;
          totalCount: number;
        }>;
      }>('/api/categories'),
    keywords: (
      id: string,
      opts: { limit?: number; minSearch?: number; includeUnanalyzed?: boolean } = {},
    ) => {
      const params = new URLSearchParams();
      params.set('limit', String(opts.limit ?? 60));
      if (opts.minSearch != null) params.set('minSearch', String(opts.minSearch));
      if (opts.includeUnanalyzed === false) params.set('includeUnanalyzed', 'false');
      return http<{
        category: { id: string; label: string; emoji: string; description: string };
        items: Array<{
          keyword: string;
          totalSearch: number;
          blogTotal: number;
          saturation: number;
          godScore: number;
          grade: string;
          verdict: string;
          analyzed: boolean;
        }>;
      }>(`/api/categories/${encodeURIComponent(id)}/keywords?${params.toString()}`);
    },
  },

  auth: {
    me: () => http<User>('/api/auth/me'),
    logout: () => http<{ loggedOut: true }>('/api/auth/logout', { method: 'POST' }),
    /** nickname 인자를 주면 신규 가입 시 그 닉네임이 저장됨 (기존 회원은 무시) */
    googleStart: (nickname?: string) => {
      const u = new URL(`${BASE || window.location.origin}/api/auth/google`);
      const n = (nickname ?? '').trim();
      if (n) u.searchParams.set('nickname', n);
      window.location.href = u.toString();
    },
  },

  keyword: {
    analyze: (keyword: string) =>
      http<KeywordAnalysisResponse>(
        `/api/keyword/analyze?q=${encodeURIComponent(keyword)}`,
      ),
    bulk: (keywords: string[]) =>
      http<{ total: number; succeeded: number; failed: number; items: BulkResultItem[] }>(
        '/api/keyword/bulk',
        { method: 'POST', body: JSON.stringify({ keywords }) },
      ),
    suggest: (q: string) =>
      http<{ q: string; suggestions: string[] }>(
        `/api/suggest?q=${encodeURIComponent(q)}`,
      ),
    /** Phase 24: SERP 영역별 문서 수 (블로그/카페/웹/뉴스/지식인/쇼핑) — 6h KV 캐시 */
    channels: (keyword: string) =>
      http<ChannelDistribution>(
        `/api/keyword/channels?q=${encodeURIComponent(keyword)}`,
      ),
    /** Phase 42: 발행 최적 요일 (DataLab 60일 요일 분포 + 추천) — 24h KV 캐시 */
    bestTime: (keyword: string) =>
      http<{
        keyword: string;
        days: number;
        dayStats: Array<{ dow: number; label: string; avgRatio: number; sampleCount: number }>;
        peakDay: { dow: number; label: string; avgRatio: number };
        lowestDay: { dow: number; label: string; avgRatio: number };
        recommendation: {
          publishDow: number;
          publishLabel: string;
          publishHourHint: string;
          reason: string;
          significance: 'high' | 'medium' | 'low';
        };
      }>(`/api/keyword/best-time?q=${encodeURIComponent(keyword)}`),
  },

  discover: {
    seed: (seed: string) =>
      http<{
        seed: string;
        /** Phase 27: fallback 적용 시 원본 입력 */
        originalSeed?: string;
        related: KeywordAnalysisResponse['relatedKeywords'];
        autocomplete: string[];
        trendingChips: string[];
      }>(`/api/discover?seed=${encodeURIComponent(seed)}`),
  },

  rank: {
    /** 즉시 1회 체크. 결과는 rank_history 에도 저장됨 (서버 측 UPSERT). */
    check: (keyword: string, url: string, channel: RankChannel = 'view') =>
      http<RankCheckResult>('/api/rank/check', {
        method: 'POST',
        body: JSON.stringify({ keyword, url, channel }),
      }),
    /** 내 추적 대상 목록 + 각각의 최신 1회 스냅샷 */
    targets: () => http<RankTargetItem[]>('/api/rank/targets'),
    /** 추적 대상 추가 (중복 시 idempotent) */
    addTarget: (keyword: string, url: string) =>
      http<{ added: true }>('/api/rank/targets', {
        method: 'POST',
        body: JSON.stringify({ keyword, url }),
      }),
    /** 추적 대상 삭제 */
    deleteTarget: (id: string) =>
      http<{ deleted: true }>(`/api/rank/targets/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    /** 30일 이력 */
    history: (keyword: string, url: string) =>
      http<RankHistoryResponse>(
        `/api/rank/history?keyword=${encodeURIComponent(keyword)}&url=${encodeURIComponent(url)}`,
      ),
  },

  golden: {
    today: () => http<GoldenTodayResponse>('/api/golden'),
    /** Phase 52: 가벼운 분포만 (대시보드 카드용) */
    distribution: () =>
      http<{
        distribution: import('@keywordgod/shared').GradeDistribution;
        previousSnapshotAt: number | null;
        generatedAt: number;
      }>('/api/golden/distribution'),
    /** Phase 52: 실시간 황금풀 (가속도 + 신선도) */
    realtime: () =>
      http<import('@keywordgod/shared').RealtimeGoldenResponse>(
        '/api/golden/realtime',
      ),
  },

  /** Phase 43: 내일 핫해질 키워드 — DataLab 14일 가속도 분석 */
  trending: {
    tomorrow: () =>
      http<{
        items: Array<{
          keyword: string;
          acc: number;
          recent7Avg: number;
          prior7Avg: number;
          totalSearch: number;
          godScore: number;
          grade: string;
          saturation: number;
        }>;
        scannedCandidates: number;
        computedAt: number;
        nextRefreshAt: number;
      }>('/api/trending/tomorrow'),
  },

  me: {
    history: () => http<SearchHistoryItem[]>('/api/me/history'),
    favorites: () => http<FavoriteItem[]>('/api/me/favorites'),
    addFavorite: (keyword: string) =>
      http<{ added: true }>('/api/me/favorites', {
        method: 'POST',
        body: JSON.stringify({ keyword }),
      }),
    removeFavorite: (keyword: string) =>
      http<{ removed: true }>(`/api/me/favorites/${encodeURIComponent(keyword)}`, {
        method: 'DELETE',
      }),
  },

  /** 운영진(admin + moderator) — 가입 승인/거절/정지 */
  mod: {
    stats: () =>
      http<{
        pending: number; active: number; suspended: number;
        admins: number; moderators: number; total: number;
      }>('/api/mod/stats'),
    pending: () => http<MemberSummary[]>('/api/mod/pending'),
    members: (q: { role?: UserRole; status?: UserStatus } = {}) => {
      const params = new URLSearchParams();
      if (q.role) params.set('role', q.role);
      if (q.status) params.set('status', q.status);
      return http<MemberSummary[]>(`/api/mod/members?${params.toString()}`);
    },
    approve: (id: string) =>
      http<{ id: string; status: 'active' }>(`/api/mod/approve/${encodeURIComponent(id)}`, { method: 'POST' }),
    reject: (id: string) =>
      http<{ id: string; status: 'suspended' }>(`/api/mod/reject/${encodeURIComponent(id)}`, { method: 'POST' }),
    suspend: (id: string) =>
      http<{ id: string; status: 'suspended' }>(`/api/mod/suspend/${encodeURIComponent(id)}`, { method: 'POST' }),
    restore: (id: string) =>
      http<{ id: string; status: 'active' }>(`/api/mod/restore/${encodeURIComponent(id)}`, { method: 'POST' }),
    /** Phase 46: 부운영자 임명/해임 (admin 전용) */
    setRole: (id: string, role: UserRole) =>
      http<{ id: string; role: UserRole }>(
        `/api/mod/members/${encodeURIComponent(id)}/role`,
        { method: 'PATCH', body: JSON.stringify({ role }) },
      ),
    /** Phase 46: 회원 만료일 설정. expiresAt=null 이면 무기한 */
    setExpires: (id: string, expiresAt: number | null) =>
      http<{ id: string; expiresAt: number | null }>(
        `/api/mod/members/${encodeURIComponent(id)}/expires`,
        { method: 'PATCH', body: JSON.stringify({ expiresAt }) },
      ),

    // Phase 49: 뉴스 자동 시드 수동 실행
    newsSeedRun: () =>
      http<{
        startedAt: number;
        finishedAt: number;
        durationMs: number;
        queriesProcessed: number;
        headlinesFetched: number;
        candidatesExtracted: number;
        inserted: number;
        errors: number;
      }>('/api/mod/news-seed', { method: 'POST' }),

    // Phase 51: Gemini AI 자동 시드 생성
    aiSeedRun: (params: { geminiApiKey: string; perDomain?: number; model?: string }) =>
      http<{
        startedAt: number;
        finishedAt: number;
        durationMs: number;
        domainsProcessed: number;
        domainsFailed: number;
        candidatesExtracted: number;
        inserted: number;
        perDomain: Array<{ id: string; label: string; got: number; error?: string }>;
      }>('/api/mod/ai-seed', { method: 'POST', body: JSON.stringify(params) }),

    // Phase 48-1: 시드 투입
    seedDefaults: () =>
      http<{ seeds: string[] }>('/api/mod/seed/defaults'),
    seedRun: (opts: {
      seeds?: string[];
      depth?: number;
      maxKeywords?: number;
      delayMs?: number;
    }) =>
      http<{
        startedAt: number;
        finishedAt: number;
        durationMs: number;
        seedsInserted: number;
        totalInserted: number;
        apiCalls: number;
        depthReached: number;
        errors: number;
        capHit: boolean;
      }>('/api/mod/seed', { method: 'POST', body: JSON.stringify(opts) }),

    // Phase 48-2: 사용 통계
    usageStats: (limit = 200) =>
      http<UsageStatsRow[]>(`/api/mod/usage-stats?limit=${limit}`),

    // Phase 48-3: 공지사항 (운영자용 - 전체 목록)
    listAnnouncements: () =>
      http<Announcement[]>('/api/mod/announcements'),
    createAnnouncement: (input: {
      title: string;
      body: string;
      level?: AnnouncementLevel;
      endsAt?: number | null;
      enabled?: boolean;
    }) =>
      http<{ id: number }>('/api/mod/announcements', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateAnnouncement: (
      id: number,
      patch: {
        title?: string;
        body?: string;
        level?: AnnouncementLevel;
        endsAt?: number | null;
        enabled?: boolean;
      },
    ) =>
      http<{ id: number }>(`/api/mod/announcements/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    deleteAnnouncement: (id: number) =>
      http<{ id: number; deleted: true }>(`/api/mod/announcements/${id}`, {
        method: 'DELETE',
      }),

    // Phase 48-4: 시즌 이벤트 편집
    createSeasonEvent: (input: {
      category: string;
      categoryLabel: string;
      name: string;
      description?: string | null;
      emoji?: string | null;
      startsMd: string;
      endsMd?: string | null;
      lookaheadDays?: number;
      recurring?: boolean;
      year?: number | null;
      keywords: string[];
      enabled?: boolean;
    }) =>
      http<{ id: number }>('/api/mod/season', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateSeasonEvent: (id: number, patch: Record<string, unknown>) =>
      http<{ id: number }>(`/api/mod/season/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    deleteSeasonEvent: (id: number) =>
      http<{ id: number; deleted: true }>(`/api/mod/season/${id}`, {
        method: 'DELETE',
      }),
  },

  /** Phase 48: 공지사항 (모든 로그인 회원) — 활성만 */
  announcements: {
    active: () => http<Announcement[]>('/api/announcements'),
  },

  /** Phase 60: 키워드 레이더 — 전 분야 폭발형/지속형 큐레이션 */
  radar: {
    get: () => http<import('@keywordgod/shared').RadarResponse>('/api/radar'),
  },

  /** Phase 61: 블로그 건강 진단 (RSS 발행 + SERP 색인율, AI 불필요) */
  blogAnalysis: {
    analyze: (url: string) =>
      http<{
        blog: { userId: string; blogName: string | null; postCount: number; collected?: number };
        publishing: {
          last7d: number; last30d: number; last90d: number;
          avgIntervalDays: number | null; lastPublishedAgoDays: number | null; oldestAgoDays: number | null;
          rssWindow: number;
        };
        topics: Array<{ category: string; count: number; pct: number }>;
        posts: Array<{ title: string; link: string; category: string | null; publishedAt: number }>;
        checkedAt: number;
      }>('/api/blog-analysis/analyze', { method: 'POST', body: JSON.stringify({ url }) }),
    indexBatch: (posts: Array<{ title: string; link: string }>, channel: 'view' | 'blog' | 'web' = 'blog') =>
      http<{
        results: Array<{ link: string; indexed: boolean; position: number | null }>;
        blocked: boolean;
      }>('/api/blog-analysis/index-batch', { method: 'POST', body: JSON.stringify({ posts, channel }) }),
    /** Phase 62: 누락 글 제목 AI 재작성 (사용자 Gemini 키) */
    retitle: (titles: string[], geminiApiKey: string) =>
      http<{ results: Array<{ original: string; suggestions: string[] }> }>(
        '/api/blog-analysis/retitle',
        { method: 'POST', body: JSON.stringify({ titles, geminiApiKey }) },
      ),
    /** Phase 64: 포스팅 패턴 분석 (본문 길이·이미지 수) */
    postStats: (userId: string, logNos: string[], category?: string) =>
      http<{
        results: Array<{ logNo: string; bodyLength: number; imageCount: number }>;
        averages: { bodyLength: number; imageCount: number };
        benchmarks: { titleLength: number; bodyLength: number; imageCount: number };
        category: string;
        sampled: number;
        valid: number;
      }>('/api/blog-analysis/post-stats', {
        method: 'POST',
        body: JSON.stringify({ userId, logNos, category }),
      }),
  },

  /** Phase 57: 블로그 누락 검출 (우리 키 풀, AI 불필요) */
  missing: {
    check: (params: { url: string; keywords: string[]; channel?: string; maxPage?: number }) =>
      http<{
        url: string;
        channel: string;
        maxPage: number;
        checkedRange: string;
        results: Array<{ keyword: string; position: number | null; page: number | null; indexed: boolean }>;
        summary: { indexed: number; missing: number; total: number; verdict: 'all_missing' | 'partial' | 'all_indexed' };
        checkedAt: number;
      }>('/api/missing/check', { method: 'POST', body: JSON.stringify(params) }),
  },

  /** Phase 57: 포스팅 품질 분석 (사용자 Gemini 키) */
  postQuality: {
    analyze: (params: { text: string; targetKeyword?: string; geminiApiKey: string; model?: string }) =>
      http<{
        stats: {
          charsWithSpace: number;
          charsNoSpace: number;
          lines: number;
          paragraphs: number;
          sentences: number;
          targetKeyword: string | null;
          keywordCount: number;
          keywordDensity: number;
        };
        analysis: {
          morphemes: Array<{ word: string; count: number }>;
          profanity: string[];
          topicSummary: string;
          topicConsistency: number;
          readabilityScore: number;
          seoSuggestions: string[];
          overallScore: number;
        };
      }>('/api/post-quality/analyze', { method: 'POST', body: JSON.stringify(params) }),
  },

  /** 관리자 로그인 / 비밀번호 변경 (쿠키 기반) */
  adminAuth: {
    me: () => http<{ authenticated: boolean }>('/api/admin/me'),
    login: (password: string) =>
      http<{ loggedIn: true; bootstrapped: boolean }>('/api/admin/login', {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),
    logout: () => http<{ loggedOut: true }>('/api/admin/logout', { method: 'POST' }),
    changePassword: (current: string, next: string) =>
      http<{ changed: true }>('/api/admin/password/change', {
        method: 'POST',
        body: JSON.stringify({ current, next }),
      }),
    collectionStatus: () =>
      http<{ paused: boolean; total: number; max: number }>('/api/admin/collection/status'),
    collectionPause: () =>
      http<{ paused: true }>('/api/admin/collection/pause', { method: 'POST' }),
    collectionResume: () =>
      http<{ paused: false }>('/api/admin/collection/resume', { method: 'POST' }),
  },

  /** 관리자(admin) — 세션 기반 admin 권한도 가능 */
  adminUsers: {
    stats: () =>
      http<{
        pending: number; active: number; suspended: number;
        admins: number; moderators: number; total: number;
      }>('/api/admin/stats'),
    members: (q: { role?: UserRole; status?: UserStatus } = {}) => {
      const params = new URLSearchParams();
      if (q.role) params.set('role', q.role);
      if (q.status) params.set('status', q.status);
      return http<MemberSummary[]>(`/api/admin/members?${params.toString()}`);
    },
    setRole: (id: string, role: UserRole) =>
      http<{ id: string; role: UserRole }>(
        `/api/admin/members/${encodeURIComponent(id)}/role`,
        { method: 'PATCH', body: JSON.stringify({ role }) },
      ),
  },
};
